import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, PlayCircle } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "./Motion";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const mediaSrc = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}/api/files/${url}`;
};

const ytEmbed = (url) => {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
};

function AdCard({ ad, index }) {
  useEffect(() => {
    if (ad.source === "manual") api.post(`/ads/${ad.id}/view`).catch(() => {});
  }, [ad.id, ad.source]);
  const trackClick = () => { if (ad.source === "manual") api.post(`/ads/${ad.id}/click`).catch(() => {}); };
  const inner = (
    <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-lg" data-testid={`ad-card-${ad.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        {ad.media_type === "image" && ad.media_url && (
          <img src={mediaSrc(ad.media_url)} alt={ad.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        {ad.media_type === "video" && ad.media_url && (
          <video src={mediaSrc(ad.media_url)} className="h-full w-full object-cover" muted loop playsInline autoPlay />
        )}
        {ad.media_type === "youtube" && ad.media_url && (
          <iframe src={ytEmbed(ad.media_url)} title={ad.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        )}
        {(ad.media_type === "auto" || !ad.media_url) && (
          <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-navy to-sky-700 text-white">
            {ad.media_type === "video" ? <PlayCircle size={34} className="opacity-90" /> : <CalendarClock size={34} className="opacity-90" />}
            <span className="mt-2 font-mono text-[11px] uppercase tracking-widest text-sky-200">{ad.source === "auto" ? "Session en ligne" : "À découvrir"}</span>
          </div>
        )}
        {ad.source === "auto" && (
          <span className="absolute left-3 top-3 rounded-full bg-emerald-500 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white">Prochaine session</span>
        )}
        {ad.source === "manual" && (
          <span className="absolute left-3 top-3 rounded-full bg-sky-500/90 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white">Sponsorisé</span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-sm font-semibold leading-snug text-navy">{ad.title}</h3>
        {ad.description && <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">{ad.description}</p>}
        <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-ciel transition-colors group-hover:text-navy">
          {ad.cta_label || "En savoir plus"} <ArrowRight size={14} />
        </span>
      </div>
    </div>
  );

  const link = ad.link_url || "";
  if (link.startsWith("/")) return <Reveal delay={index * 0.06}><Link to={link} onClick={trackClick} className="block h-full">{inner}</Link></Reveal>;
  if (link.startsWith("http")) return <Reveal delay={index * 0.06}><a href={link} onClick={trackClick} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a></Reveal>;
  return <Reveal delay={index * 0.06}>{inner}</Reveal>;
}

export function AdSlots({ title = "À la une", eyebrow = "Publicités & sessions", limit = 3, dark = false, carousel = false }) {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    api.get(`/ads/active?limit=${limit}`).then(({ data }) => setAds(data)).catch(() => setAds([]));
  }, [limit]);

  if (ads.length === 0) return null;

  const useCarousel = carousel && ads.length > 3;

  return (
    <section className={dark ? "bg-navy py-16 text-white" : "border-b border-slate-200 bg-slate-50 py-16"} data-testid="ad-slots">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-ciel">{eyebrow}</p>
          <h2 className={`mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
        </Reveal>
        {useCarousel ? (
          <Carousel opts={{ align: "start", loop: false }} className="mt-10 px-1" data-testid="ad-carousel">
            <CarouselContent>
              {ads.map((ad, i) => (
                <CarouselItem key={ad.id} className="sm:basis-1/2 lg:basis-1/3">
                  <AdCard ad={ad} index={i} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious data-testid="ad-carousel-prev" className={dark ? "border-white/30 bg-white/10 text-white hover:bg-white/20" : ""} />
            <CarouselNext data-testid="ad-carousel-next" className={dark ? "border-white/30 bg-white/10 text-white hover:bg-white/20" : ""} />
          </Carousel>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ads.map((ad, i) => <AdCard key={ad.id} ad={ad} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}
